import { Grid } from '@material-ui/core';
import { Button, SelectField, TextField } from '@roc/ui';
import { useLoanStore } from '@roc/feature-loans';
import { observer } from 'mobx-react';
import { useCallback, useEffect, useState } from 'react';
import { DelinquencyService } from '../delinquencyService';

export interface TitleAgencyInfo {
  titleAgencyId: number | null;
  name: string;
  alias: string;
  email: string;
  phone: string;
  state: string;
}

export interface TitleAgentFormData {
  titleAgencyId: number | null;
  field1: string;
  field2: string;
  field3: string;
  field4: string;
}

export interface TitleAgentFormProps {
  closeModal: () => void;
  setIsLoading?: (key: string, value: boolean) => void;
  reloadLoan?: (rerenderGrid?: boolean) => void;
  showAlert: (message: string, type: 'success' | 'danger') => void;
}

const delinquencyService = new DelinquencyService();

function getEntityArray(data: Array<{ titleAgencyId: number; name: string }>): { label: string; value: number }[] {
  return data.map((item) => ({
    label: item.name ?? '',
    value: item.titleAgencyId,
  }));
}

export const TitleAgentForm = observer(({
  closeModal,
  setIsLoading,
  reloadLoan,
  showAlert,
}: TitleAgentFormProps) => {
  const { loanStore } = useLoanStore();
  const loan = loanStore?.loanDetails;

  const [titleAgencyList, setTitleAgencyList] = useState<{ label: string; value: number }[]>([]);
  const [titleAgencyId, setTitleAgencyId] = useState<number | null>(null);
  const [optionValue, setOptionValue] = useState<string>('');
  const [field1, setField1] = useState('');
  const [field2, setField2] = useState('');
  const [field3, setField3] = useState('');
  const [field4, setField4] = useState('');

  const foreclosureTitleAgentId = loan?.loanClosingData?.foreclosureTitleAgentId;

  useEffect(() => {
    delinquencyService
      .getTitleAgencyList()
      .then((res) => {
        const list = res?.data ?? [];
        setTitleAgencyList(getEntityArray(Array.isArray(list) ? list : []));
      })
      .catch(() => {
        showAlert('Error while fetching the title agency list', 'danger');
      });
    if (foreclosureTitleAgentId != null) {
      setOptionValue(String(foreclosureTitleAgentId));
      setTitleAgencyId(foreclosureTitleAgentId);
      delinquencyService
        .getTitleAgencyById(foreclosureTitleAgentId)
        .then((res) => {
          const d = res?.data ?? {};
          setField1(d?.alias ?? d?.name ?? '');
          setField2(d?.email ?? '');
          setField3(d?.phone ?? '');
          setField4(d?.state ?? '');
        })
        .catch(() => {
          showAlert('Error while fetching the title agency information', 'danger');
        });
    }
  }, [foreclosureTitleAgentId, showAlert]);

  const handleTitleAgencyChange = useCallback(
    async (value: number) => {
      setTitleAgencyId(value);
      setOptionValue(String(value));
      setIsLoading?.('isLoading', true);
      try {
        const res = await delinquencyService.getTitleAgencyById(value);
        const d = res?.data ?? {};
        setField1(d?.alias ?? d?.name ?? '');
        setField2(d?.email ?? '');
        setField3(d?.phone ?? '');
        setField4(d?.state ?? '');
      } catch {
        showAlert('Error while fetching the title agency information', 'danger');
      }
      setIsLoading?.('isLoading', false);
    },
    [setIsLoading, showAlert]
  );

  const handleSave = useCallback(async () => {
    if (loan?.loanId == null) return;
    setIsLoading?.('isLoading', true);
    if (titleAgencyId == null) {
      showAlert('Title Agency missing, please select one from the list', 'danger');
      setIsLoading?.('isLoading', false);
      return;
    }
    try {
      await delinquencyService.updateTitleAgentFormV2(loan.loanId, { titleAgencyId });
      showAlert('Title Agency was successfully updated', 'success');
      closeModal();
      reloadLoan?.(false);
    } catch {
      showAlert('Error while updating the title agency information', 'danger');
    }
    setIsLoading?.('isLoading', false);
  }, [
    titleAgencyId,
    loan?.loanId,
    closeModal,
    reloadLoan,
    showAlert,
    setIsLoading,
  ]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <SelectField
          label="Title Agency"
          value={optionValue}
          options={titleAgencyList}
          onChange={(val) => handleTitleAgencyChange(Number(val))}
          disabled={titleAgencyList.length === 0}
          testId="title-agent-select"
          fullWidth
          variant="outlined"
          margin="normal"
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          variant="outlined"
          label="Alias"
          value={field1}
          onChange={(e) => setField1(e.target.value)}
          fullWidth
          margin="normal"
          testId="title-agent-alias"
          disabled
        />
        <TextField
          variant="outlined"
          label="Email"
          value={field2}
          onChange={(e) => setField2(e.target.value)}
          fullWidth
          margin="normal"
          testId="title-agent-email"
          disabled
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          variant="outlined"
          label="Phone"
          value={field3}
          onChange={(e) => setField3(e.target.value)}
          fullWidth
          margin="normal"
          testId="title-agent-phone"
          disabled
        />
        <TextField
          variant="outlined"
          label="State"
          value={field4}
          onChange={(e) => setField4(e.target.value)}
          fullWidth
          margin="normal"
          testId="title-agent-state"
          disabled
        />
      </Grid>
      <Grid container spacing={2} justifyContent="center">
        <Grid item>
          <Button
            type="button"
            variant="contained"
            color="default"
            testId="title-agent-cancel"
            onClick={closeModal}
          >
            Cancel
          </Button>
        </Grid>
        <Grid item>
          <Button
            type="button"
            variant="contained"
            color="primary"
            testId="title-agent-save"
            onClick={handleSave}
          >
            Save
          </Button>
        </Grid>
      </Grid>
    </Grid>
  );
});
